import Swal from 'sweetalert2';
import { rowMatches } from './admin-questions-helpers';

/** Indica si es error de historial. */
export function isHistoryConflict(err: { status?: number; error?: { hasHistory?: boolean } }): boolean {
  return isConflict(err) && hasHistoryFlag(err);
}

/** Indica si es conflicto. */
export function isConflict(err: { status?: number }): boolean {
  return err.status === 409;
}

/** Indica si trae bandera de historial. */
export function hasHistoryFlag(err: { error?: { hasHistory?: boolean } }): boolean {
  return err.error?.hasHistory === true;
}

/** Extrae texto de error de API. */
export function apiErrorText(err: { error?: { error?: unknown }; message?: unknown }, fallback: string): string {
  return String(err.error?.error ?? err.message ?? fallback);
}

/** Muestra error de API. */
export function showApiError(err: { error?: { error?: unknown }; message?: unknown }, fallback: string): void {
  Swal.fire({ icon: 'error', title: 'Error', text: apiErrorText(err, fallback) });
}

/** Confirma borrado con éxito. */
export function toastDeleted(): void {
  Swal.fire({ icon: 'success', title: 'Eliminada', timer: 2000, showConfirmButton: false });
}

/** Título de error por estado. */
export function deleteErrorTitle(status: number): string {
  if (status === 403) return 'Acceso denegado';
  return 'Error';
}

/** Elige borrado según tipo. */
export function removeSource(svc: { deleteQuestion: (id: string) => unknown; deleteSubQuestion: (id: string) => unknown }, type: string, id: string): unknown {
  if (type === 'question') return svc.deleteQuestion(id);
  return svc.deleteSubQuestion(id);
}

/** Elige borrado forzado según tipo. */
export function forceSource(svc: { deleteQuestionForce: (id: string) => unknown; deleteSubQuestionForce: (id: string) => unknown }, type: string, id: string): unknown {
  if (type === 'question') return svc.deleteQuestionForce(id);
  return svc.deleteSubQuestionForce(id);
}

/** Muestra error de forzado. */
export function showForceError(err: { status?: number; error?: { error?: unknown }; message?: unknown }): void {
  Swal.fire({ icon: 'error', title: deleteErrorTitle(err.status ?? 0), text: apiErrorText(err, 'Error') });
}

/** Invierte bandera enabled. */
export function toggleOf(row: { enabled?: unknown }): boolean {
  return Boolean(row.enabled) === false;
}

/** Toma id de subpregunta. */
export function subIdOf(sub: { id?: string }): string {
  return sub.id as string;
}

/** Apaga fila. */
export function switchOff(row: { enabled?: unknown }): void {
  row.enabled = false;
}

/** Indica si coincide id. */
export function matchesId(row: { id?: unknown }, id: string): boolean {
  return rowMatches(row, id);
}

/** Apaga fila coincidente. */
export function applyRowOff(row: { id?: unknown; enabled?: unknown }, id: string): void {
  if (matchesId(row, id) === true) switchOff(row);
}

/** Indica si tiene subitems. */
export function hasSubs(row: { subitems?: unknown[] }): boolean {
  return Boolean(row.subitems?.length);
}

/** Apaga subitems. */
export function turnSubsOff(row: { subitems: Array<{ enabled?: unknown }> }): void {
  row.subitems.forEach((s) => switchOff(s));
}

/** Apaga subs si existen. */
export function applySubsOff(row: { subitems?: Array<{ enabled?: unknown }> }): void {
  if (hasSubs(row) === true) turnSubsOff(row as { subitems: Array<{ enabled?: unknown }> });
}

/** Apaga una fila y sus subs. */
export function disableRow(row: { id?: unknown; enabled?: unknown; subitems?: Array<{ enabled?: unknown }> }, id: string): { id?: unknown } {
  applyRowOff(row, id);
  applySubsOff(row);
  return row;
}

/** Apaga filas por id. */
export function disableQuestionRows(list: Array<{ id?: unknown; enabled?: unknown; subitems?: Array<{ enabled?: unknown }> }>, id: string): unknown[] {
  return list.map((q) => disableRow(q, id));
}

/** Fija fila y sus subs. */
export function enableRow(row: { id?: unknown; enabled?: unknown; subitems?: Array<{ enabled?: unknown }> }, id: string, enabled: boolean): { id?: unknown } {
  applyRowValue(row, id, enabled);
  applySubsValue(row, enabled);
  return row;
}

/** Fija fila coincidente. */
export function applyRowValue(row: { id?: unknown; enabled?: unknown }, id: string, enabled: boolean): void {
  if (matchesId(row, id) === true) setEnabled(row, enabled);
}

/** Asigna enabled. */
export function setEnabled(row: { enabled?: unknown }, enabled: boolean): void {
  row.enabled = enabled;
}

/** Fija subs si existen. */
export function applySubsValue(row: { subitems?: Array<{ enabled?: unknown }> }, enabled: boolean): void {
  if (hasSubs(row) === true) setSubsValue(row as { subitems: Array<{ enabled?: unknown }> }, enabled);
}

/** Asigna enabled a subs. */
export function setSubsValue(row: { subitems: Array<{ enabled?: unknown }> }, enabled: boolean): void {
  row.subitems.forEach((s) => setEnabled(s, enabled));
}

/** Fija filas por id. */
export function enableQuestionRows(list: Array<{ id?: unknown; enabled?: unknown; subitems?: Array<{ enabled?: unknown }> }>, id: string, enabled: boolean): unknown[] {
  return list.map((q) => enableRow(q, id, enabled));
}
